import { useEffect, useRef } from 'react';
import Highcharts, { Options } from 'highcharts/highstock';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';
import { DateInterval, ReportItemTypeInfo, StandardReportType } from 'types/reporting';
import { useGetChartFromReportSpec } from 'query/reporting';
import { FormattedMessage } from 'react-intl';

interface ReportChartProps {
    reportType: StandardReportType;
    studyId: number;
    itemId: number;
    dateInterval: DateInterval;
    spec: ReportItemTypeInfo;
    initialOptions: Options;
}

export function ReportSpecChart({ spec, reportType, studyId, itemId, dateInterval, initialOptions }: ReportChartProps) {
    const ref = useRef<HighchartsReactRefObject>(null);

    const { data, isFetching, isError } = useGetChartFromReportSpec({
        studyId: studyId,
        itemId: itemId,
        reportType: reportType,
        dateInterval: dateInterval,
        spec: spec,
    });

    useEffect(() => {
        if (ref.current && data) {
            const chart = ref.current.chart;
            chart.hideData();

            // Remove all existing series
            while (chart.series.length > 0) {
                chart.series[0].remove(false);
            }

            // Add new series from data
            data.series?.forEach((s) => {
                chart.addSeries(s, false);
            });

            chart.setTitle(data.title);
            // Redraw the chart once after all updates
            chart.redraw();
        }
    }, [ref, data]);

    useEffect(() => {
        if (ref.current) {
            isFetching ? ref.current.chart.showLoading() : ref.current.chart.hideLoading();
        }
    }, [ref, isFetching]);

    if (isError) {
        return <FormattedMessage id="common_generic_loading_error" />;
    }

    return (
        <HighchartsReact
            ref={ref}
            options={initialOptions}
            highcharts={Highcharts}
            containerProps={{ style: { width: '100%', height: '60vh', minHeight: '400px' } }}
            constructorType={'stockChart'}
        />
    );
}
